import * as KpiSelectors from './selectors/kpi.selectors';
import {filter, take} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import * as fromKpi from './reducers/kpi.reducers';
import * as KpiActions from './actions/kpi.actions';
import {KpiRefreshService} from './services/kpi.refresh.service';
import * as Highcharts from 'highcharts';
import {KpiStatUpdated} from './actions/kpi.actions';

export const revenueFormatter = function () {
  return this.series.name + ': ' + '\u20AC' + Highcharts.numberFormat(this.y, 2, ',', '.');
};

export class KpiHelper {
  public subs = [];
  public changedFlag;

  public revenueFormatter = revenueFormatter;

  constructor(
    private store: Store<fromKpi.KpiState>,
    private refreshService: KpiRefreshService,
  ) {}

  manageData(requiredData, tabName, expirationMinutes) {
    this.subs.push(
      this.refreshService.watchRefresh().subscribe(shouldRefresh => {
        if (shouldRefresh) {
          this.loadAllStats(requiredData, tabName);
        }
      }),
    );

    this.subs.push(
      this.store
        .select(KpiSelectors.selectKpiFetchTime, {tab: tabName})
        .pipe(take(1))
        .subscribe(refreshTime => {
          const timeDifference = Math.floor((Date.now() - refreshTime) / 60000);

          if (!refreshTime || timeDifference >= expirationMinutes) {
            this.loadAllStats(requiredData, tabName);
          }
        }),
    );
  }

  loadAllStats(requiredData, tab) {
    const dispatch = stat => {
      this.store.dispatch(
        new KpiActions.KpiStatRequested({
          statName: stat.statName,
          param: stat.filter,
          dateRange: stat.dateRange,
          tab: tab,
        }),
      );
    };

    if (Array.isArray(requiredData)) {
      requiredData.forEach(stat => {
        dispatch(stat);
      });
    } else {
      dispatch({...requiredData, tab});
    }
  }

  prepareChartRangeData(chartData, stats, readableNames?) {
    // group data, and add or replace with old
    if (!chartData) {
      chartData = {
        labels: [],
        dataSets: [],
      };
    }

    const groups = [];

    stats.forEach(stat => {
      if (groups.indexOf(stat.grouped_by) === -1) {
        groups.push(stat.grouped_by);
      }
    });

    groups.forEach(group => {
      const name = readableNames ? readableNames[stats[0].stat_name] : group;

      const newStat = {
        name: name,
        data: stats.filter(obj => obj.grouped_by === group).map(stat => [stat.day, parseFloat(stat.value)]),
      };

      const oldStatId = chartData.dataSets.find(stat => stat.name === name);

      if (oldStatId) {
        chartData.dataSets[oldStatId] = newStat;
      } else {
        chartData.dataSets.push(newStat);
      }
    });

    this.changedFlag = {refresh: true};
    return JSON.parse(JSON.stringify(chartData));
  }

  removeDuplicates(data) {
    // remove duplicates, change date to timestamp and sort
    const cachedObject = {};
    data.map(
      stat =>
        (cachedObject[stat.day + stat.grouped_by] = Object.assign({}, stat, {
          day: Date.parse(stat.day.replace(/-/g, '/').replace(/[a-z]+/gi, ' ')),
        })),
    );
    return Object.values(cachedObject).sort((a, b) => a['day'] - b['day']);
  }

  fillEmptyPoints(data, tabName) {
    const filledData = JSON.parse(JSON.stringify(data));
    const requiredData = {
      groupedBy: new Set(filledData.map(stat => stat.grouped_by)),
      dates: new Set(filledData.map(stat => stat.day)),
    };

    requiredData.groupedBy.forEach(group => {
      requiredData.dates.forEach(day => {
        if (filledData.findIndex(obj => obj.day === day && obj.grouped_by === group) === -1) {
          filledData.push({stat_name: filledData[0].stat_name, grouped_by: group, day: day, value: 0});
        }
      });
    });

    if (data.length !== filledData.length) {
      this.store.dispatch(
        new KpiStatUpdated({
          value: filledData,
          statName: filledData[0].stat_name,
          tabName: tabName,
        }),
      );
    }
    return filledData;
  }

  getStoredData(tabName, statName) {
    return this.store
      .select(KpiSelectors.selectKpiStat, {tabName: tabName, statName: statName})
      .pipe(filter(res => res));
  }
}
